/**
 * `/designs` — the DTF design library (Shopify → DTF; catalog-mapping v1.1).
 *
 * A design holds two pre-rasterized transparent PNGs — an ADULT file (required) and an optional
 * YOUTH file — each authored at its exact print size (1 in = 300 px). The backend validates each
 * file asynchronously (transparent RGBA) and flips it Ready / Rejected, and renders a lightweight
 * preview WebP the table + review lightbox load instead of the multi-MB print PNG. Designs are NEVER
 * scaled at print time (halftones can't be resampled), which is why youth is its own file.
 *
 * Per-design row actions (⋯): add a youth file later, replace a band, rename, or delete (delete is
 * refused while a product mapping still references the design). Click a thumbnail to review it large.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Crosshair,
  ExternalLink,
  ImagePlus,
  Layers,
  Loader2,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { UploadProgress } from '@/components/upload-progress';
import { fetchAllDesigns } from '@/lib/api/designs';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import { AlignCanvas } from './design-align-canvas';
import { decodeArtForAlignment, PRINT_DPI } from './design-align-decode';
import {
  type AlignBox,
  alignmentIsStale,
  boxInInches,
  isWholeFile,
  offsetFromFileCentreIn,
  proposeAlignment,
  type StoredAlignment,
  sameBox,
  type TopGap,
  toBox,
  WHOLE_FILE,
} from './design-align-lib';
import {
  DESIGN_STATUS_META,
  type DesignBand,
  type DesignFileView,
  type DesignSortMode,
  type DesignSummary,
  hasRecentPending,
  isValidStyleCode,
  matchesDesignSearch,
  normalizeStyleCode,
  PREVIEW_BACKGROUNDS,
  type PreviewBackground,
  printSizeLabel,
  sortDesigns,
  UploadCancelledError,
  uploadBandFile,
} from './design-lib';
import { DesignSpecEditor } from './design-spec-editor';
import { screenCountLabel } from './screen-print-spec-lib';

export function DesignsPage() {
  const client = useApiClient();

  const query = useQuery({
    queryKey: ['designs'],
    // Load ALL designs (follow the cursor) so nothing past the first 100 (name-sorted) is hidden.
    queryFn: () => fetchAllDesigns(client),
    // Poll while a freshly-uploaded file (either band) is still validating (bounded — a row
    // stuck PENDING past the window is treated as a failed upload, not polled).
    refetchInterval: (q) => (hasRecentPending(q.state.data ?? [], Date.now()) ? 4000 : false),
  });

  const designs = query.data ?? [];
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<DesignSortMode>('style');
  const filtered = sortDesigns(
    designs.filter((d) => matchesDesignSearch(d, search)),
    sort,
  );
  const flaggedCount = designs.filter((d) => d.problem).length;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Design library</h1>
          <p className="text-sm text-muted-foreground">
            Transparent PNGs authored at print size (1 in = 300 px). Adult + optional youth file per
            design — never scaled, so youth is its own file. Uploads validate automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={cn('size-4', query.isFetching && 'animate-spin')} />
            Refresh
          </Button>
          <NewDesignDialog />
        </div>
      </header>

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load designs</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}

      {query.isPending && <Skeleton className="h-64 w-full" />}

      {query.isSuccess &&
        (designs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative max-w-xs flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or style code…"
                  className="pl-8"
                  aria-label="Search designs"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Sort</span>
                <ToggleGroup
                  type="single"
                  size="sm"
                  value={sort}
                  onValueChange={(v) => v && setSort(v as DesignSortMode)}
                  aria-label="Sort designs"
                >
                  <ToggleGroupItem value="style">Style</ToggleGroupItem>
                  <ToggleGroupItem value="name">Name</ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
            {flaggedCount > 0 && (
              <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                <AlertTriangle className="size-4" />
                {flaggedCount} design{flaggedCount === 1 ? '' : 's'} flagged with a problem —
                blocked from printing until resolved.
              </p>
            )}
            {filtered.length === 0 ? (
              <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
                No designs match “{search}”.
              </p>
            ) : (
              <DesignTable designs={filtered} />
            )}
          </div>
        ))}
    </div>
  );
}

/**
 * The preview image for a band: loads the lightweight WebP, falling back to the full-res PNG if the
 * WebP 404s (thumbnail not rendered yet, or a design uploaded before the thumbnail worker existed).
 * Shows a placeholder when the band has no validated file.
 */
function PreviewImage({
  file,
  alt,
  className,
}: {
  file: DesignFileView | undefined;
  alt: string;
  className?: string;
}) {
  const [useOriginal, setUseOriginal] = useState(false);
  const preview = file?.previewUrl;
  const original = file?.originalUrl;
  const src = useOriginal ? original : (preview ?? original);
  if (!src) return <ImagePlus className="size-4 text-muted-foreground" />;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={className}
      onError={() => {
        if (!useOriginal && original) setUseOriginal(true);
      }}
    />
  );
}

/** One band's status badge + derived print size (or a validation error / "—" when absent). */
function BandCell({ file }: { file: DesignFileView | undefined }) {
  if (!file) return <span className="text-muted-foreground">—</span>;
  const meta = DESIGN_STATUS_META[file.status];
  const size = printSizeLabel(file);
  return (
    <div className="space-y-1">
      <Badge variant="outline" className={meta.className}>
        {meta.label}
      </Badge>
      {size && <div className="text-xs tabular-nums text-muted-foreground">{size}</div>}
      {file.status === 'INVALID' && file.validationError && (
        <p className="max-w-xs text-xs text-destructive">{file.validationError}</p>
      )}
    </div>
  );
}

/** Which section the Edit dialog opens on: the name/code details, or the screen-print spec (P2). */
type EditTab = 'details' | 'spec';

type RowDialog =
  | { kind: 'band'; band: DesignBand }
  | { kind: 'align' }
  | { kind: 'edit'; tab?: EditTab }
  | { kind: 'problem' }
  | { kind: 'delete' }
  | null;

function DesignRow({ design }: { design: DesignSummary }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [dialog, setDialog] = useState<RowDialog>(null);
  const close = () => setDialog(null);

  return (
    <tr
      className={cn(
        'border-t',
        design.problem ? 'bg-destructive/5' : '[&:nth-child(even)]:bg-muted/20',
      )}
    >
      <td className="px-3 py-2 align-top">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            title="Review design"
            className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-neutral-950 transition hover:ring-2 hover:ring-ring"
          >
            <PreviewImage
              file={design.adult}
              alt={design.name}
              className="size-full object-contain"
            />
          </button>
          <div className="min-w-0 space-y-1">
            <span className="font-medium">{design.name}</span>
            {design.problem && (
              <div className="flex items-start gap-1.5 text-destructive text-xs">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  <span className="font-semibold">Problem:</span> {design.problem.note}
                </span>
              </div>
            )}
            {screenCountLabel(design.screenPrintSpec) && (
              <Badge variant="outline" className="font-normal text-muted-foreground">
                Screen print · {screenCountLabel(design.screenPrintSpec)}
              </Badge>
            )}
            <AlignmentNote design={design} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        {design.styleCode ? (
          <span className="inline-block rounded border px-1.5 py-0.5 font-mono text-xs font-semibold tracking-wide">
            {design.styleCode}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <BandCell file={design.adult} />
      </td>
      <td className="px-3 py-2 align-top">
        <BandCell file={design.youth} />
      </td>
      <td className="px-3 py-2 align-top text-right">
        <DesignRowActions design={design} onSelect={setDialog} />
      </td>

      {lightboxOpen && <DesignLightbox design={design} onClose={() => setLightboxOpen(false)} />}
      {dialog?.kind === 'band' && (
        <BandUploadDialog design={design} band={dialog.band} onClose={close} />
      )}
      {dialog?.kind === 'align' && <AlignDesignDialog design={design} onClose={close} />}
      <EditDialog
        design={design}
        open={dialog?.kind === 'edit'}
        initialTab={dialog?.kind === 'edit' ? dialog.tab : undefined}
        onClose={close}
      />
      <ProblemDialog design={design} open={dialog?.kind === 'problem'} onClose={close} />
      <DeleteDialog design={design} open={dialog?.kind === 'delete'} onClose={close} />
    </tr>
  );
}

/**
 * Re-align an EXISTING design (plan § 4.3) — the action the backfill runs on, and the one that fixes a
 * box marked against art that has since been replaced.
 *
 * Two different URLs, for a reason worth keeping: the canvas DISPLAYS the CDN preview (small, cached,
 * instant), but the slice-profile suggestion decodes the presigned ORIGINAL. The preview rides
 * CloudFront, whose cache key excludes `Origin` (`HeaderBehavior: none`) under a one-year immutable
 * TTL — so a preview already fetched by a plain `<img>` is cached WITHOUT `access-control-allow-origin`
 * and a later pixel read of it fails CORS, unpredictably and for a year. Measured against prod: priming
 * a key with no Origin header then re-requesting it with one returns `x-cache: Hit` and no ACAO. The
 * presigned S3 URL skips CloudFront entirely, so the bucket's own CORS rule applies every time — and it
 * is the exact raster the box gets stamped against.
 */
function AlignDesignDialog({ design, onClose }: { design: DesignSummary; onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const bands = (['adult', 'youth'] as const).filter((b) => design[b]?.status === 'READY');
  const stored = (b: DesignBand): StoredAlignment | undefined =>
    design.alignment?.[b] as StoredAlignment | undefined;
  const [boxes, setBoxes] = useState<Record<string, AlignBox>>(() =>
    Object.fromEntries(bands.map((b) => [b, toBox(stored(b))])),
  );
  const [proposals, setProposals] = useState<Record<string, Proposal | null>>({});
  const [reading, setReading] = useState(true);

  // Decode the originals once, in the background: the canvas is usable immediately and the
  // suggestion appears when it lands. A failure is not an error here — the human can still drag.
  // The design is fixed for the life of this dialog, and the list query refetches give it a new object
  // identity on every poll — depending on it would re-download the full print PNG each time.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one decode pass per opened dialog
  useEffect(() => {
    let live = true;
    (async () => {
      const next: Record<string, Proposal | null> = {};
      for (const b of bands) {
        const url = design[b]?.originalUrl;
        const wIn = design[b]?.printWidthIn;
        const hIn = design[b]?.printHeightIn;
        if (!url || !wIn || !hIn) {
          next[b] = null;
          continue;
        }
        try {
          const res = await fetch(url);
          const decoded = res.ok ? await decodeArtForAlignment(await res.blob()) : null;
          const profile = decoded
            ? proposeAlignment(decoded.alpha, decoded.widthPx, decoded.heightPx, wIn, hIn)
            : null;
          next[b] = profile ? { box: profile.box, topGap: profile.topGap ?? null } : null;
        } catch {
          next[b] = null;
        }
      }
      if (live) {
        setProposals(next);
        setReading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      // Send only the bands that actually changed; `null` CLEARS one back to the whole file.
      const alignment: Record<string, unknown> = {};
      for (const b of bands) {
        const box = boxes[b] ?? WHOLE_FILE;
        const before = toBox(stored(b));
        if (sameBox(box, before)) continue;
        alignment[b] = isWholeFile(box)
          ? null
          : {
              ...box,
              ...(design[b]?.widthPx && design[b]?.heightPx
                ? { forWidthPx: design[b]?.widthPx, forHeightPx: design[b]?.heightPx }
                : {}),
            };
      }
      if (Object.keys(alignment).length === 0) return;
      const res = await client.PATCH('/designs/{designId}', {
        params: { path: { designId: design.designId } },
        body: { alignment },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Could not save the alignment.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Align — {design.name}</DialogTitle>
          <DialogDescription>
            The press guides line up with this box, not the whole file. Narrow it when a logo or an
            edge element sits apart from the artwork; leave it as the whole file otherwise.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
          {bands.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No validated file to align yet. Upload one first.
            </p>
          )}
          {bands.map((b) => {
            const file = design[b];
            const wIn = file?.printWidthIn ?? 0;
            const hIn = file?.printHeightIn ?? 0;
            const box = boxes[b] ?? WHOLE_FILE;
            const proposal = proposals[b];
            // One button, the fullest suggestion: the top-gap box carries the horizontal profile too.
            const suggestion = proposal?.topGap?.box ?? proposal?.box ?? null;
            const inches = boxInInches(box, wIn, hIn);
            const offset = offsetFromFileCentreIn(box, wIn);
            return (
              <section key={b} className="space-y-2" data-testid={`align-row-${b}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h3 className="font-medium text-sm capitalize">{b}</h3>
                  <p className="font-mono text-muted-foreground text-xs">
                    {fmtIn(wIn)} × {fmtIn(hIn)} in
                  </p>
                </div>
                {alignmentIsStale(stored(b), file) && (
                  <Alert>
                    <AlertDescription>
                      This box was marked against a different version of this file — check it before
                      saving.
                    </AlertDescription>
                  </Alert>
                )}
                {file?.previewUrl && wIn > 0 && hIn > 0 ? (
                  <>
                    <div className="h-[420px]">
                      <AlignCanvas
                        src={file.previewUrl}
                        fileWIn={wIn}
                        fileHIn={hIn}
                        box={box}
                        onChange={(next) => setBoxes((prev) => ({ ...prev, [b]: next }))}
                        disabled={mutation.isPending}
                        label={`${b} alignment box`}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={mutation.isPending || isWholeFile(box)}
                        onClick={() => setBoxes((prev) => ({ ...prev, [b]: WHOLE_FILE }))}
                      >
                        Whole file
                      </Button>
                      {suggestion && !isWholeFile(suggestion) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={mutation.isPending}
                          onClick={() => setBoxes((prev) => ({ ...prev, [b]: suggestion }))}
                        >
                          Use the suggestion
                        </Button>
                      )}
                      {reading && (
                        <span className="text-muted-foreground text-xs">Reading the file…</span>
                      )}
                      <p className="font-mono text-muted-foreground text-xs">
                        {fmtIn(inches.wIn)} × {fmtIn(inches.hIn)} in · centre{' '}
                        {fmtIn(inches.centreIn)} in from the left edge
                      </p>
                    </div>
                    {proposal?.topGap && <TopGapNote gap={proposal.topGap} />}
                    {Math.abs(offset) >= 0.05 && (
                      <p className="text-muted-foreground text-xs">
                        The press will put this {fmtIn(Math.abs(offset))} in{' '}
                        {offset > 0 ? 'right' : 'left'} of where it would put the whole file.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    This band has no preview or no known print size, so it cannot be aligned here.
                  </p>
                )}
              </section>
            );
          })}
          {mutation.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error instanceof Error ? mutation.error.message : 'Could not save.'}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || bands.length === 0}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Save alignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The alignment state on a library row. Silent for the common case — most designs want the whole file
 * and an unset box behaves exactly as it always has, so an "unaligned" badge on every row would be
 * noise. It speaks up only when a box exists, and loudly when one was marked against art that has
 * since been replaced: nobody opens a dialog to check, so a stale box has to announce itself here.
 */
function AlignmentNote({ design }: { design: DesignSummary }) {
  const bands = (['adult', 'youth'] as const).filter((b) => design.alignment?.[b]);
  if (bands.length === 0) return null;
  const stale = bands.filter((b) =>
    alignmentIsStale(design.alignment?.[b] as StoredAlignment | undefined, design[b]),
  );
  if (stale.length > 0) {
    return (
      <div className="flex items-start gap-1.5 text-amber-600 text-xs dark:text-amber-500">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Aligned against an earlier version of the {stale.join(' and ')} file — re-check it.
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
      <Crosshair className="size-3.5 shrink-0" />
      <span>Aligned{bands.length === 2 ? '' : ` (${bands[0]})`}</span>
    </div>
  );
}

function DesignRowActions({
  design,
  onSelect,
}: {
  design: DesignSummary;
  onSelect: (d: RowDialog) => void;
}) {
  const hasYouth = design.youth != null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Design actions">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {!hasYouth && (
          <DropdownMenuItem onClick={() => onSelect({ kind: 'band', band: 'youth' })}>
            <ImagePlus className="size-4" />
            Add youth file
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onSelect({ kind: 'band', band: 'adult' })}>
          <Upload className="size-4" />
          Replace adult file
        </DropdownMenuItem>
        {hasYouth && (
          <DropdownMenuItem onClick={() => onSelect({ kind: 'band', band: 'youth' })}>
            <Upload className="size-4" />
            Replace youth file
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onSelect({ kind: 'align' })}>
          <Crosshair className="size-4" />
          Align to the press guides
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSelect({ kind: 'edit' })}>
          <Pencil className="size-4" />
          Edit name & code
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSelect({ kind: 'edit', tab: 'spec' })}>
          <Layers className="size-4" />
          Screen print spec
        </DropdownMenuItem>
        <DropdownMenuItem
          className={design.problem ? 'text-destructive focus:text-destructive' : undefined}
          onClick={() => onSelect({ kind: 'problem' })}
        >
          <AlertTriangle className="size-4" />
          {design.problem ? 'Problem flagged — review' : 'Flag a problem'}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => onSelect({ kind: 'delete' })}
        >
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesignTable({ designs }: { designs: DesignSummary[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Design
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Style</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Adult</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Youth</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground sr-only">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {designs.map((d) => (
            <DesignRow key={d.designId} design={d} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Full-size review of a design — the preview WebP on a switchable background, per band. */
function DesignLightbox({ design, onClose }: { design: DesignSummary; onClose: () => void }) {
  const hasYouth = design.youth != null;
  const [band, setBand] = useState<DesignBand>('adult');
  const [bg, setBg] = useState<PreviewBackground>('black');
  const file = band === 'youth' ? design.youth : design.adult;
  const size = file ? printSizeLabel(file) : null;
  const meta = file ? DESIGN_STATUS_META[file.status] : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{design.name}</DialogTitle>
          <DialogDescription>
            Reviewing the {band} file. Transparent art — pick a background to check it against.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {hasYouth ? (
            <ToggleGroup
              type="single"
              size="sm"
              value={band}
              onValueChange={(v) => v && setBand(v as DesignBand)}
            >
              <ToggleGroupItem value="adult">Adult</ToggleGroupItem>
              <ToggleGroupItem value="youth">Youth</ToggleGroupItem>
            </ToggleGroup>
          ) : (
            <span className="text-sm text-muted-foreground">Adult file</span>
          )}
          <ToggleGroup
            type="single"
            size="sm"
            value={bg}
            onValueChange={(v) => v && setBg(v as PreviewBackground)}
          >
            {(Object.keys(PREVIEW_BACKGROUNDS) as PreviewBackground[]).map((k) => (
              <ToggleGroupItem key={k} value={k}>
                {PREVIEW_BACKGROUNDS[k].label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div
          className={cn(
            'flex min-h-[20rem] items-center justify-center overflow-hidden rounded-lg border',
            PREVIEW_BACKGROUNDS[bg].className,
          )}
        >
          {file?.status === 'READY' ? (
            <PreviewImage
              file={file}
              alt={`${design.name} (${band})`}
              className="max-h-[60vh] w-auto object-contain"
            />
          ) : (
            <p className="p-8 text-sm text-muted-foreground">
              {file ? DESIGN_STATUS_META[file.status].label : 'No youth file for this design.'}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            {meta && (
              <Badge variant="outline" className={meta.className}>
                {meta.label}
              </Badge>
            )}
            {size && <span className="tabular-nums text-muted-foreground">{size}</span>}
          </div>
          {file?.originalUrl && (
            <a
              href={file.originalUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              View full-res PNG
              <ExternalLink className="size-3.5" />
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Add a youth file to an adult-only design, or replace an existing band's file. */
function BandUploadDialog({
  design,
  band,
  onClose,
}: {
  design: DesignSummary;
  band: DesignBand;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isAddYouth = band === 'youth' && design.youth == null;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a PNG to upload.');
      const controller = new AbortController();
      abortRef.current = controller;
      setProgress(0);
      await uploadBandFile(client, design.designId, band, file, {
        onProgress: (f) => setProgress(f),
        signal: controller.signal,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onClose();
    },
    onSettled: () => setProgress(null),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isAddYouth ? 'Add youth file' : `Replace ${band} file`} — {design.name}
          </DialogTitle>
          <DialogDescription>
            {band === 'youth'
              ? 'A transparent PNG authored at its YOUTH print size (1 in = 300 px) — never the adult art scaled down.'
              : 'A transparent PNG authored at its exact print size (1 in = 300 px).'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="band-file">{band === 'youth' ? 'Youth' : 'Adult'} PNG</Label>
            <Input
              id="band-file"
              type="file"
              accept="image/png"
              disabled={mutation.isPending}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {!isAddYouth && (
            <p className="text-xs text-muted-foreground">
              The current {band} file stays in place until the new one finishes validating.
            </p>
          )}
          {mutation.isPending && progress !== null && (
            <UploadProgress label={`Uploading ${band} file…`} fraction={progress} />
          )}
          {mutation.isError && !(mutation.error instanceof UploadCancelledError) && (
            <Alert variant="destructive">
              <AlertDescription>
                {mutation.error instanceof Error ? mutation.error.message : 'Upload failed.'}
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          {mutation.isPending && (
            <Button variant="outline" onClick={() => abortRef.current?.abort()}>
              Cancel
            </Button>
          )}
          <Button onClick={() => mutation.mutate()} disabled={file == null || mutation.isPending}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Upload {band} file
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  design,
  open,
  initialTab = 'details',
  onClose,
}: {
  design: DesignSummary;
  open: boolean;
  /** Which section opens — the name/code details, or the screen-print spec (plan P2). */
  initialTab?: EditTab;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [tab, setTab] = useState<EditTab>(initialTab);
  const [name, setName] = useState(design.name);
  const [styleCode, setStyleCode] = useState(design.styleCode ?? '');
  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setName(design.name);
      setStyleCode(design.styleCode ?? '');
    }
  }, [open, initialTab, design.name, design.styleCode]);

  const trimmedName = name.trim();
  const codeValid = isValidStyleCode(styleCode);
  const changed = trimmedName !== design.name || styleCode !== (design.styleCode ?? '');
  const canSave = trimmedName.length > 0 && codeValid && changed;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!trimmedName) throw new Error('Name cannot be empty.');
      if (!codeValid) {
        throw new Error(
          'Style code: up to 12 uppercase letters, digits, or dashes (e.g. FR, LT-001).',
        );
      }
      // Always send name; send styleCode as the current value ('' clears it server-side).
      const res = await client.PATCH('/designs/{designId}', {
        params: { path: { designId: design.designId } },
        body: { name: trimmedName, styleCode },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Save failed.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent className={tab === 'spec' ? 'sm:max-w-4xl' : undefined}>
        <DialogHeader>
          <DialogTitle>Edit design</DialogTitle>
          <DialogDescription>
            {tab === 'spec'
              ? 'The screen room’s technical sheet — colours in print order, heads, ink, mesh, frame, cure. The uploaded files are untouched.'
              : 'Change the name or the style code — the uploaded files are untouched.'}
          </DialogDescription>
        </DialogHeader>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={tab}
          onValueChange={(v) => {
            if (v === 'details' || v === 'spec') setTab(v);
          }}
          aria-label="Edit design section"
          className="justify-start"
        >
          <ToggleGroupItem value="details">Name &amp; code</ToggleGroupItem>
          <ToggleGroupItem value="spec">
            Screen print
            {design.screenPrintSpec ? ` · ${design.screenPrintSpec.screens.length}` : ''}
          </ToggleGroupItem>
        </ToggleGroup>
        {tab === 'spec' ? (
          // Scroll lives on this inner div, never on DialogContent — the close button must stay put.
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <DesignSpecEditor design={design} onSaved={onClose} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && canSave && !mutation.isPending && mutation.mutate()
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-style-code">Style code</Label>
              <Input
                id="edit-style-code"
                value={styleCode}
                onChange={(e) => setStyleCode(normalizeStyleCode(e.target.value))}
                onKeyDown={(e) =>
                  e.key === 'Enter' && canSave && !mutation.isPending && mutation.mutate()
                }
                placeholder="FR, FW, LC, or LT-001"
                className="font-mono"
                aria-invalid={!codeValid}
              />
              <p className="text-xs text-muted-foreground">
                The short SKU code shown on the label — fronts FR / FW / LC, backs LT-001. Leave
                blank for none.
              </p>
              {!codeValid && (
                <p className="text-sm text-destructive">
                  Up to 12 uppercase letters, digits, or dashes.
                </p>
              )}
            </div>
            {mutation.isError && (
              <p className="text-sm text-destructive">
                {mutation.error instanceof Error ? mutation.error.message : 'Save failed.'}
              </p>
            )}
          </div>
        )}
        {tab === 'details' && (
          <DialogFooter>
            <Button onClick={() => mutation.mutate()} disabled={!canSave || mutation.isPending}>
              {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Flag / resolve a design problem. Flagging BLOCKS the design from batching (server gate) so a bad
 * film can't be reprinted while a file replacement lags; the designer replaces the file, then resolves.
 * Any signed-in staff can flag; the note + who/when is shown so the designer knows what's wrong.
 */
function ProblemDialog({
  design,
  open,
  onClose,
}: {
  design: DesignSummary;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [note, setNote] = useState(design.problem?.note ?? '');
  useEffect(() => {
    if (open) setNote(design.problem?.note ?? '');
  }, [open, design.problem?.note]);

  const flagged = design.problem != null;

  const flagMutation = useMutation({
    mutationFn: async () => {
      const trimmed = note.trim();
      if (!trimmed) throw new Error('Describe the problem.');
      const res = await client.POST('/designs/{designId}/problem', {
        params: { path: { designId: design.designId } },
        body: { note: trimmed },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Could not flag the design.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onClose();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async () => {
      const res = await client.DELETE('/designs/{designId}/problem', {
        params: { path: { designId: design.designId } },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Could not resolve the problem.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onClose();
    },
  });

  const busy = flagMutation.isPending || resolveMutation.isPending;
  const err = flagMutation.error ?? resolveMutation.error;
  const unchanged = flagged && note.trim() === (design.problem?.note ?? '');

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          flagMutation.reset();
          resolveMutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{flagged ? 'Design problem' : 'Flag a problem'}</DialogTitle>
          <DialogDescription>
            A flagged design is blocked from batching — it can't be printed until it's resolved, so
            the designer replaces the file first.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {flagged && design.problem && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <div className="flex items-center gap-1.5 font-medium text-destructive">
                <AlertTriangle className="size-4" /> Currently flagged
              </div>
              <p className="mt-1">{design.problem.note}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Flagged {new Date(design.problem.flaggedAt).toLocaleString()}
                {design.problem.flaggedBy ? ` · ${design.problem.flaggedBy}` : ''}
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="problem-note">{flagged ? 'Update the note' : "What's wrong?"}</Label>
            <Textarea
              id="problem-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. registration off · colors wrong · low-res file"
              rows={3}
              disabled={busy}
            />
          </div>
          {err && (
            <p className="text-sm text-destructive">
              {err instanceof Error ? err.message : 'Something went wrong.'}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {flagged ? (
            <Button variant="outline" onClick={() => resolveMutation.mutate()} disabled={busy}>
              {resolveMutation.isPending && <Loader2 className="size-4 animate-spin" />}
              Resolve (file replaced)
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant={flagged ? 'default' : 'destructive'}
            onClick={() => flagMutation.mutate()}
            disabled={busy || note.trim().length === 0 || unchanged}
          >
            {flagMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {flagged ? 'Update note' : 'Flag problem'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  design,
  open,
  onClose,
}: {
  design: DesignSummary;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.DELETE('/designs/{designId}', {
        params: { path: { designId: design.designId } },
      });
      // A 409 (design still mapped to products) surfaces its detail — the operator must unmap first.
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Delete failed.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete “{design.name}”?</DialogTitle>
          <DialogDescription>
            This permanently removes the design and its uploaded files. If a product mapping still
            uses it, the delete is blocked until you remove it from those mappings.
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn't delete</AlertTitle>
            <AlertDescription>
              {mutation.error instanceof Error ? mutation.error.message : 'Delete failed.'}
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Delete design
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <ImagePlus className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">No designs yet</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Upload a transparent PNG authored at its exact print size (1 in = 300 px). Add a youth-size
        file too if the product is printed on youth garments.
      </p>
    </div>
  );
}

interface BandArt {
  /** Object URL for the picked File — revoked when the dialog resets. */
  url: string;
  /** True print size from the file's own pixels at 300 DPI. */
  wIn: number;
  hIn: number;
  /** The file's real raster, recorded on the box so a later re-upload can flag it as stale. */
  widthPx: number;
  heightPx: number;
  /** The slice-profile suggestion, or null when the file could not be decoded. */
  proposal: AlignBox | null;
  /** A stray above the art with clear space under it (plan § 13) — a note + suggestion, never a seed. */
  topGap: TopGap | null;
}

/** What the re-align dialog decodes per band: the same two things, from the presigned original. */
type Proposal = { box: AlignBox; topGap: TopGap | null };

const fmtIn = (v: number) => String(Number(v.toFixed(2)));

/**
 * The one vertical shape the profile flags (plan § 13): a small stray mark above the art — a speck,
 * a dotted hairline, dust — with clear space under it. Nearly every press rule hangs the design's TOP
 * edge a fixed distance below the collar, so the beam measures to the mark and the art prints low by
 * the gap plus the mark. Advisory: step 1 of § 13 never seeds a box from it.
 */
function TopGapNote({ gap }: { gap: TopGap }) {
  return (
    <Alert data-testid="top-gap-note">
      <AlertDescription>
        A stray mark sits above the art — {fmtIn(gap.elementHIn)} in tall, with {fmtIn(gap.gapIn)}{' '}
        in of clear space under it. A top-anchored press guide would hang the design from the mark
        and print the art {fmtIn(gap.gapIn + gap.elementHIn)} in low. The suggestion starts the box
        below it.
      </AlertDescription>
    </Alert>
  );
}

/** Stamp the raster the box was marked against, so a re-upload of different art reads as stale. */
function withRaster(box: AlignBox, art: BandArt | undefined) {
  return art && art.widthPx > 0
    ? { ...box, forWidthPx: art.widthPx, forHeightPx: art.heightPx }
    : box;
}

/** Read a picked PNG: its true print size, plus a proposed alignment box from the slice profile. */
async function readBandArt(file: File): Promise<BandArt> {
  const url = URL.createObjectURL(file);
  const decoded = await decodeArtForAlignment(file);
  if (!decoded) {
    return { url, wIn: 0, hIn: 0, widthPx: 0, heightPx: 0, proposal: null, topGap: null };
  }
  const wIn = decoded.naturalWidthPx / PRINT_DPI;
  const hIn = decoded.naturalHeightPx / PRINT_DPI;
  const profile = proposeAlignment(decoded.alpha, decoded.widthPx, decoded.heightPx, wIn, hIn);
  return {
    url,
    wIn,
    hIn,
    widthPx: decoded.naturalWidthPx,
    heightPx: decoded.naturalHeightPx,
    proposal: profile?.box ?? null,
    topGap: profile?.topGap ?? null,
  };
}

/** One band's canvas plus its readouts. */
function AlignBand({
  title,
  art,
  box,
  onChange,
  disabled,
}: {
  title: string;
  art: BandArt;
  box: AlignBox;
  onChange: (next: AlignBox) => void;
  disabled: boolean;
}) {
  const inches = boxInInches(box, art.wIn, art.hIn);
  const offset = offsetFromFileCentreIn(box, art.wIn);
  // One button, the fullest suggestion: the top-gap box carries the horizontal profile too. The SEED
  // (goToAlign) stays the horizontal proposal alone — § 13 step 1 never seeds a vertical narrowing.
  const suggestion = art.topGap?.box ?? art.proposal;
  const canProfile = art.wIn > 0 && art.hIn > 0;
  return (
    <section className="space-y-2" data-testid={`align-band-${title.toLowerCase()}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="font-medium text-sm">{title}</h3>
        <p className="font-mono text-muted-foreground text-xs">
          {canProfile ? `${fmtIn(art.wIn)} × ${fmtIn(art.hIn)} in` : 'size unknown'}
        </p>
      </div>
      {canProfile ? (
        <>
          <div className="h-[420px]">
            <AlignCanvas
              src={art.url}
              fileWIn={art.wIn}
              fileHIn={art.hIn}
              box={box}
              onChange={onChange}
              disabled={disabled}
              label={`${title} alignment box`}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || isWholeFile(box)}
              onClick={() => onChange(WHOLE_FILE)}
            >
              Whole file
            </Button>
            {suggestion && !isWholeFile(suggestion) && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled}
                onClick={() => onChange(suggestion)}
              >
                Use the suggestion
              </Button>
            )}
            <p className="font-mono text-muted-foreground text-xs">
              {fmtIn(inches.wIn)} × {fmtIn(inches.hIn)} in · centre {fmtIn(inches.centreIn)} in from
              the left edge
            </p>
          </div>
          {art.topGap && <TopGapNote gap={art.topGap} />}
          {Math.abs(offset) >= 0.05 && (
            <p className="text-muted-foreground text-xs">
              The press will put this {fmtIn(Math.abs(offset))} in {offset > 0 ? 'right' : 'left'}{' '}
              of where it would put the whole file.
            </p>
          )}
        </>
      ) : (
        <p className="text-muted-foreground text-sm">
          This file could not be read here, so it will press as it always has — the whole file
          centred. Nothing is blocked; the upload continues.
        </p>
      )}
    </section>
  );
}

function NewDesignDialog() {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'details' | 'align'>('details');
  const [name, setName] = useState('');
  const [styleCode, setStyleCode] = useState('');
  const [hasYouth, setHasYouth] = useState(false);
  const [adultFile, setAdultFile] = useState<File | null>(null);
  const [youthFile, setYouthFile] = useState<File | null>(null);
  const [art, setArt] = useState<{ adult?: BandArt; youth?: BandArt }>({});
  const [align, setAlign] = useState<{ adult: AlignBox; youth: AlignBox }>({
    adult: WHOLE_FILE,
    youth: WHOLE_FILE,
  });
  const [reading, setReading] = useState(false);
  const [upload, setUpload] = useState<{ label: string; fraction: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const codeValid = isValidStyleCode(styleCode);
  const detailsValid =
    name.trim().length > 0 && codeValid && adultFile != null && (!hasYouth || youthFile != null);

  function revokeArt() {
    for (const band of [art.adult, art.youth]) if (band) URL.revokeObjectURL(band.url);
  }

  function reset() {
    revokeArt();
    setStep('details');
    setName('');
    setStyleCode('');
    setHasYouth(false);
    setAdultFile(null);
    setYouthFile(null);
    setArt({});
    setAlign({ adult: WHOLE_FILE, youth: WHOLE_FILE });
    setUpload(null);
  }

  /**
   * Decode both picked files and open the align step with a box already proposed. Pre-filled rather
   * than blank on purpose: most designs want the whole file, and a step people must fill from scratch
   * is a step they learn to click through (plan § 4.2).
   */
  async function goToAlign() {
    if (!adultFile) return;
    setReading(true);
    try {
      const adult = await readBandArt(adultFile);
      const youth = hasYouth && youthFile ? await readBandArt(youthFile) : undefined;
      setArt({ adult, youth });
      setAlign({
        adult: adult.proposal ?? WHOLE_FILE,
        // Seed youth from the adult box, then make someone look: measured across the library the two
        // bands agree within 1% of width on 133 of 135 designs, but `American Icon` differs by
        // 0.14 in on the youth print — enough to matter, which is why this is a seed and not a copy.
        youth: youth?.proposal ?? adult.proposal ?? WHOLE_FILE,
      });
      setStep('align');
    } finally {
      setReading(false);
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!adultFile) throw new Error('Choose an adult PNG to upload.');
      if (hasYouth && !youthFile) throw new Error('Choose a youth PNG or turn off the youth file.');
      // Only send a box that actually narrows something — an unset band keeps today's behaviour, and
      // storing the whole file would make "unset" unreadable later.
      const alignment = {
        ...(isWholeFile(align.adult) ? {} : { adult: withRaster(align.adult, art.adult) }),
        ...(hasYouth && youthFile && !isWholeFile(align.youth)
          ? { youth: withRaster(align.youth, art.youth) }
          : {}),
      };
      const created = await client.POST('/designs', {
        body: {
          name: name.trim(),
          hasYouth,
          ...(styleCode ? { styleCode } : {}),
          ...(Object.keys(alignment).length > 0 ? { alignment } : {}),
        },
      });
      if (created.error || !created.data?.ok) {
        throw new Error(created.error?.error?.detail ?? 'Could not create the design.');
      }
      const { designId } = created.data.data;

      // Upload each band to its own presigned key. Sequential so a youth failure surfaces cleanly;
      // the bar relabels adult → youth so the operator sees which file is transferring. One
      // AbortController spans both bands so a single Cancel stops whichever is in flight.
      const controller = new AbortController();
      abortRef.current = controller;
      const total = hasYouth && youthFile ? 2 : 1;
      const doUpload = async (uploadBand: DesignBand, f: File, index: number) => {
        const label =
          total > 1
            ? `Uploading ${uploadBand} file (${index} of ${total})…`
            : `Uploading ${uploadBand} file…`;
        setUpload({ label, fraction: 0 });
        await uploadBandFile(client, designId, uploadBand, f, {
          onProgress: (fr) => setUpload({ label, fraction: fr }),
          signal: controller.signal,
        });
      };
      await doUpload('adult', adultFile, 1);
      if (hasYouth && youthFile) await doUpload('youth', youthFile, 2);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      reset();
      setOpen(false);
      mutation.reset();
    },
    onSettled: () => setUpload(null),
  });

  const busy = mutation.isPending || reading;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && mutation.isPending) return;
        setOpen(o);
        if (!o) {
          reset();
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Upload className="size-4" />
          New design
        </Button>
      </DialogTrigger>
      <DialogContent className={step === 'align' ? 'max-w-3xl' : undefined}>
        <DialogHeader>
          <DialogTitle>
            {step === 'details' ? 'Upload a design' : 'Mark the part that lines up'}
          </DialogTitle>
          <DialogDescription>
            {step === 'details'
              ? 'Transparent PNGs authored at print size (1 in = 300 px). Add a separate youth file if this art prints on youth garments — it is never scaled from the adult art.'
              : 'The press guides line up with this box, not the whole file. Most designs want the whole file; narrow it when a logo or an edge element sits apart from the artwork.'}
          </DialogDescription>
        </DialogHeader>
        {step === 'details' ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="design-name">Name</Label>
              <Input
                id="design-name"
                value={name}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
                placeholder="Fuego front logo"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="design-style-code">
                Style code <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="design-style-code"
                value={styleCode}
                disabled={busy}
                onChange={(e) => setStyleCode(normalizeStyleCode(e.target.value))}
                placeholder="FR, FW, LC, or LT-001"
                className="font-mono"
                aria-invalid={!codeValid}
              />
              {!codeValid ? (
                <p className="text-destructive text-sm">
                  Up to 12 uppercase letters, digits, or dashes.
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  The short SKU code shown on the label — you can also add it later.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="design-adult">Adult PNG</Label>
              <Input
                id="design-adult"
                type="file"
                accept="image/png"
                disabled={busy}
                onChange={(e) => setAdultFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={hasYouth}
                disabled={busy}
                onChange={(e) => setHasYouth(e.target.checked)}
              />
              This design also has a youth-size file
            </label>
            {hasYouth && (
              <div className="space-y-1.5">
                <Label htmlFor="design-youth">Youth PNG</Label>
                <Input
                  id="design-youth"
                  type="file"
                  accept="image/png"
                  disabled={busy}
                  onChange={(e) => setYouthFile(e.target.files?.[0] ?? null)}
                />
              </div>
            )}
          </div>
        ) : (
          // The scroll lives here, never on DialogContent — the close control has to stay outside it.
          <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
            {art.adult && (
              <AlignBand
                title="Adult"
                art={art.adult}
                box={align.adult}
                disabled={mutation.isPending}
                onChange={(next) => setAlign((a) => ({ ...a, adult: next }))}
              />
            )}
            {art.youth && (
              <AlignBand
                title="Youth"
                art={art.youth}
                box={align.youth}
                disabled={mutation.isPending}
                onChange={(next) => setAlign((a) => ({ ...a, youth: next }))}
              />
            )}
            {mutation.isPending && upload && (
              <UploadProgress label={upload.label} fraction={upload.fraction} />
            )}
            {mutation.isError && !(mutation.error instanceof UploadCancelledError) && (
              <Alert variant="destructive">
                <AlertDescription>
                  {mutation.error instanceof Error ? mutation.error.message : 'Upload failed.'}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
        <DialogFooter>
          {mutation.isPending && (
            <Button variant="outline" onClick={() => abortRef.current?.abort()}>
              Cancel
            </Button>
          )}
          {step === 'align' && !mutation.isPending && (
            <Button variant="outline" onClick={() => setStep('details')}>
              Back
            </Button>
          )}
          {step === 'details' ? (
            <Button onClick={goToAlign} disabled={!detailsValid || busy}>
              {reading && <Loader2 className="size-4 animate-spin" />}
              Next — align
            </Button>
          ) : (
            <Button onClick={() => mutation.mutate()} disabled={busy}>
              {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
              Upload design
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
